<script setup>
import {computed} from "vue";
import {useRoute} from "vue-router";
import {useBookingStore} from "@/stores/booking";
import AppIcon from "@/components/AppIcon.vue";
import {useI18n} from "vue-i18n";

const route = useRoute()
const bookingStore = useBookingStore()
const i18n = useI18n()

const DONE_ROUTE = 'booking.checkin.done'

const items = computed(() => {
  const isCheckedIn = bookingStore.isCheckedIn
  const onDoneScreen = route.name === DONE_ROUTE

  return [
    {name: 'booking.home', icon: 'home', label: i18n.t('nav.start')},
    {
      // The confirmation screen is the last step of the check-in flow, so the
      // tab stays visible there — otherwise no tab is marked as current.
      name: onDoneScreen ? DONE_ROUTE : 'booking.checkin',
      icon: 'how_to_reg',
      label: i18n.t('checkin'),
      hidden: isCheckedIn && !onDoneScreen,
      badge: !isCheckedIn,
    },
    {name: 'booking.doorkeys', icon: 'key', label: i18n.t('doorKeys')},
    {name: 'booking.guestfolder', icon: 'menu_book', label: i18n.t('nav.info')},
    {name: 'booking.contact', icon: 'forum', label: i18n.t('nav.contact')},
  ].filter(item => !item.hidden)
})

function isActive(item) {
  return route.name === item.name
}
</script>

<template>
  <nav class="app-nav" :aria-label="$t('nav.label')">
    <router-link
        v-for="item in items"
        :key="item.name"
        :to="{name: item.name}"
        class="app-nav__tab"
        :class="{'app-nav__tab--active': isActive(item)}"
        :aria-current="isActive(item) ? 'page' : undefined"
    >
      <span class="app-nav__icon-wrap">
        <app-icon :name="item.icon" :size="24"/>
        <span v-if="item.badge" class="app-nav__badge"></span>
      </span>
      <span class="app-nav__label">{{ item.label }}</span>
    </router-link>
  </nav>
</template>

<style scoped>
.app-nav__tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px 2px 7px;
  border-radius: 13px;
  background: transparent;
  color: var(--ww-nav-inactive);
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.app-nav__tab--active {
  background: var(--ww-primary-tint);
  color: var(--ww-primary-dark);
}

.app-nav__icon-wrap {
  position: relative;
  display: grid;
  place-items: center;
}

.app-nav__badge {
  position: absolute;
  top: -1px;
  right: -6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ww-warn);
  border: 2px solid var(--ww-surface);
  box-sizing: content-box;
}

.app-nav__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
</style>
